/**
 * The record screen's route registration (frame `65:2`), discovered by `../routes.ts`'s glob.
 * Creation has no route of its own: `NewCampaignDialog` opens over whatever route
 * triggered it.
 *
 * The record route is campaign-scoped and carries the one campaign-nav entry
 * this file registers -- the same split monitoring's and teams' own
 * `routes.ts` make between a nav-level screen and one reached only by
 * clicking through it. `./outOfGroup.routes.ts`'s route is the same kind of
 * click-through: it carries no nav entry of its own, only `outOfGroupRoute`
 * here.
 */

import { createElement, type ReactNode } from 'react';
import { IdCard } from 'lucide-react';
import { useParams } from '@tanstack/react-router';

import type { RouteBundle } from '../../routes/contract';
import { useCampaignOrganisationId } from '../../session/orgContext';
import { CampaignRecord } from './CampaignRecord';
import { outOfGroupRoute } from './outOfGroup.routes';
import { RECORD_PATH } from './paths';
import { rm } from './recordMessages';

const CONTROL_PLANE_READ = 'control_plane.read';

function useParamCampaignId(): string {
  const { campaignId } = useParams({ strict: false }) as { campaignId?: string };
  return campaignId ?? '';
}

function CampaignRecordRoute(): ReactNode {
  const campaignId = useParamCampaignId();
  const tenantId = useCampaignOrganisationId();
  if (tenantId === null) return null;
  return createElement(CampaignRecord, { campaignId, tenantId });
}

export const bundle: RouteBundle = {
  routes: [
    {
      path: RECORD_PATH,
      element: createElement(CampaignRecordRoute),
      requires: CONTROL_PLANE_READ,
      kind: 'form',
    },
    outOfGroupRoute,
  ],
  nav: [],
  campaignNav: [
    {
      path: RECORD_PATH,
      // Reuses the page's own section heading -- the nav tab and the
      // section it opens onto read the same, like `Monitoramento` does not
      // need a second string next to `MonitoringOverview`'s own.
      label: () => rm('section.details'),
      requires: CONTROL_PLANE_READ,
      icon: IdCard,
      order: 90,
      kind: 'form',
    },
  ],
};
